import React, { createContext, useEffect, useState, useRef } from "react";
import { handleApiError } from "../../constants/Constants";
import {
  dashboardMonthlyUser,
  dashboardRecommandedStatus,
  dashBoardUserData,
} from "../../api/Api";
import CommentIcon from "../../assets/images/arrowDown.png";

// Import BubbleController specifically
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  Tooltip,
  Legend,
  PointElement,
  BubbleController, // <-- Import BubbleController
} from "chart.js";
import { Bar, Chart, Doughnut } from "react-chartjs-2";

export const DashBoardContext = createContext({});
export const DashBoardContextProvider = ({ children }) => {
  const [selectedYear, setSelectedYear] = useState("");
  const [yearList, setYearList] = useState([]);
  const [spinnerStatus, setSpinnerStatus] = useState(false);
  const [statusValue, setStatusValue] = useState(0);
  const [errorPopup, setErrorPopup] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState("");
  const [monthWiseData, setMonthWiseData] = useState([]);
  const [buttonText, setButtonText] = useState("");
  const [recommandedStatus, setRecommandedStatus] = useState();
  const [recommandedStatusBattery, setRecommandedStatusBattery] = useState();
  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
  const [userData, setUserData] = useState([]);
  const [totalPage, setTotalPage] = useState(0);
  const [pageCount, setPageCount] = useState(1);

  const labels = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const dataValues = monthWiseData?.map((item) => {
    return item.count;
  });

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  // Register all necessary components, including BubbleController
  ChartJS.register(
    BarElement,
    ArcElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
    PointElement,
    BubbleController // <-- Register BubbleController here
  );

  const data = {
    labels,
    datasets: [
      {
        label: "Visitors",
        data: dataValues,
        backgroundColor: "rgba(253, 180, 38, 1)",
        borderColor: "#FF6D00",
        borderWidth: 0,
        borderRadius: 10,
        barThickness: 10,
        order: 1,
      },
      {
        label: "Top Points",
        data: dataValues, // Adjust position
        backgroundColor: "rgba(233, 103, 26, 1)",
        borderColor: "rgba(233, 103, 26, 1)",
        pointRadius: 5,
        hoverRadius: 1,
        type: "bubble", // Correctly set to bubble chart
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          title: (context) => `${context[0].label}`,
          label: (context) => `${context.raw} Visitors`,
        },
        displayColors: false,
        backgroundColor: "#FFF",
        titleColor: "rgba(125, 143, 179, 1)",
        bodyColor: "rgba(253, 180, 38, 1)",
        borderColor: "rgba(228, 228, 233, 1)",
        borderWidth: 1,
        titleFont: {
          size: 13, // Font size for title
          weight: "normal", // Font weight for title
        },
        bodyFont: {
          size: 16, // Font size for body
          weight: "normal", // Font weight for body
        },
      },
      legend: {
        display: false,
      },
    },
    elements: {
      bar: {
        borderSkipped: false,
      },
    },

    scales: {
      x: {
        // When mixing bar and bubble charts, the scales can get tricky.
        // For a bubble chart, you typically need a 'linear' scale.
        // For a bar chart, 'category' or 'linear' (if continuous) is used.
        // If your X-axis is categorical (like months), stick with CategoryScale.
        // If the bubble chart needs linear X, you might need two X-axes or rethink the chart combination.
        // display: false,
        beginAtZero: true,
        grid: {
          display: false,
        },
      },
      y: {
        // display: false, // Hide y-axis
        beginAtZero: true,
        ticks: {
          callback: function (value, index, values) {
            return "";
          },
        },
      },
    },
  };
  /***doughnut chart */
  const [hoverIndex, setHoverIndex] = useState(null);

  const generateDynamicJson = () => {
    const startYear = 2024;
    const currentYear = new Date().getFullYear();
    const years = [];

    for (let year = currentYear; year >= startYear; year--) {
      years.push({
        _id: year.toString(),
        name: year.toString(),
      });
    }

    setYearList(years);
  };

  const viewMonthlyUserData = async () => {
    try {
      setSpinnerStatus(true);
      const getDetailData = await dashboardMonthlyUser(selectedYear);
      if (getDetailData.status === 200) {
        setSpinnerStatus(false);
        setErrorPopup(false);
        setMonthWiseData(getDetailData.data.data.monthlyUsers);
      }
    } catch (error) {
      if (error?.status === 404) {
        setErrorPopupMessage("");
        setErrorPopup(false);
      } else {
        handleApiError(
          error,
          setStatusValue,
          setErrorPopupMessage,
          setErrorPopup,
          setButtonText
        );
      }
    }
    setSpinnerStatus(false);
  };

  const viewRecommandUserData = async () => {
    try {
      setSpinnerStatus(true);
      const getDetailData = await dashboardRecommandedStatus();
      if (getDetailData.status === 200) {
        setSpinnerStatus(false);
        setErrorPopup(false);
        setRecommandedStatus(getDetailData.data.data.topProducts.topInverters);
        setRecommandedStatusBattery(
          getDetailData.data.data.topProducts.topBatteries
        );
      }
    } catch (error) {
      if (error?.status === 404) {
        setErrorPopupMessage("");
        setErrorPopup(false);
      } else {
        handleApiError(
          error,
          setStatusValue,
          setErrorPopupMessage,
          setErrorPopup,
          setButtonText
        );
      }
    }
    setSpinnerStatus(false);
  };

  const viewUserData = async () => {
    try {
      setSpinnerStatus(true);
      const getDetailData = await dashBoardUserData(pageCount);
      if (getDetailData.status === 200) {
        setSpinnerStatus(false);
        setErrorPopup(false);
        setTotalPage(getDetailData.data.data.contactedUsers?.totalPages);
        setUserData(
          getDetailData.data.data.contactedUsers?.users.map((item) => ({
            ...item,
            name: item?.userDetails?.name ? item?.userDetails?.name : "-",
            phone: item?.userDetails?.phone ? item?.userDetails?.phone : "-",
            email: item?.userDetails?.email ? item?.userDetails?.email : "-",
            state: item?.userDetails?.state ? item?.userDetails?.state : "-",
            city: item?.userDetails?.city ? item?.userDetails?.city : "-",
            comment: (
              <div className="commentSection">
                {item?.userDetails?.comment
                  ? item?.userDetails?.comment
                  : "No comment available"}
              </div>
            ),
          }))
        );
      }
    } catch (error) {
      if (error?.status === 404) {
        setErrorPopupMessage("");
        setErrorPopup(false);
      } else {
        handleApiError(
          error,
          setStatusValue,
          setErrorPopupMessage,
          setErrorPopup,
          setButtonText
        );
      }
    }
    setSpinnerStatus(false);
  };

  const invertersData = recommandedStatus || [];
  const totalInverters = invertersData.reduce(
    (sum, inverter) => sum + inverter.count,
    0
  );

  const batteryData = recommandedStatusBattery || [];
  const totalBattery = batteryData.reduce(
    (sum, battery) => sum + battery.count,
    0
  );

  const dataProducts = {
    labels: recommandedStatus?.map((item) => {
      return item?.modelName;
    }),
    datasets: [
      {
        data: recommandedStatus?.map((item) => {
          return item?.count;
        }),
        data: invertersData?.map((inv) =>
          ((inv.count / totalInverters) * 100).toFixed(1)
        ),
        backgroundColor: [
          "rgba(253, 180, 38, 1)",
          "rgba(89, 211, 224, 1)",
          "rgba(78, 149, 255, 1)",
          "rgba(175, 41, 195, 1)",
          "rgba(130, 41, 255, 1)",
          "rgba(38, 70, 253, 1)",
        ],
        borderWidth: 0,
        hoverBorderWidth: 20, // <- thick border ring on hover
        borderColor: [
          "rgba(253, 180, 38, 1)",
          "rgba(89, 211, 224, 1)",
          "rgba(78, 149, 255, 1)",
          "rgba(175, 41, 195, 1)",
          "rgba(130, 41, 255, 1)",
          "rgba(38, 70, 253, 1)",
        ],
        cutout: "70%",
        hoverOffset: 0, // makes hovered section pop out
      },
    ],
  };

  const dataProductsBattery = {
    labels: recommandedStatusBattery?.map((item) => {
      return item?.modelName;
    }),
    datasets: [
      {
        data: recommandedStatusBattery?.map((item) => {
          return item?.count;
        }),
        data: batteryData?.map((inv) =>
          ((inv.count / totalBattery) * 100).toFixed(1)
        ),
        backgroundColor: [
          "rgba(253, 180, 38, 1)",
          "rgba(89, 211, 224, 1)",
          "rgba(78, 149, 255, 1)",
          "rgba(175, 41, 195, 1)",
          "rgba(130, 41, 255, 1)",
          "rgba(38, 70, 253, 1)",
        ],
        borderWidth: 0,
        hoverBorderWidth: 20, // <- thick border ring on hover
        borderColor: [
          "rgba(253, 180, 38, 1)",
          "rgba(89, 211, 224, 1)",
          "rgba(78, 149, 255, 1)",
          "rgba(175, 41, 195, 1)",
          "rgba(130, 41, 255, 1)",
          "rgba(38, 70, 253, 1)",
        ],
        cutout: "70%",
        hoverOffset: 0, // makes hovered section pop out
      },
    ],
  };

  const [centerValue, setCenterValue] = useState({
    label: dataProducts?.labels,
    value: `${dataProducts?.datasets[0]?.data[0]}%`,
  });

  const [centerValueBattery, setCenterValueBattery] = useState({
    label: dataProductsBattery?.labels,
    value: `${dataProductsBattery?.datasets[0]?.data[0]}%`,
  });

  const optionsProducts = {
    plugins: {
      tooltip: {
        enabled: false,
        // callbacks: {
        //   label: (ctx) => `${ctx.label}: ${ctx.raw}%`,
        // },
      },
      legend: { display: false },
    },
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      easing: "easeOutCubic",
      duration: 200, // total chart load animation
    },
    hover: {
      animationDuration: 0, // <- remove delay on hover!
    },
    layout: {
      padding: 30, // <-- fixes clipping during hover
    },
    onHover: (event, chartElement) => {
      if (chartElement.length) {
        const index = chartElement[0].index;
        setHoverIndex(index);
        setCenterValue({
          label: dataProducts.labels[index],
          value: `${dataProducts.datasets[0].data[index]}%`,
        });
      } else {
        setHoverIndex(null);
        setCenterValue({
          label: dataProducts?.labels[0],
          value: `${dataProducts?.datasets[0]?.data[0]}%`,
        });
      }
    },
  };

  useEffect(() => {
    if (
      dataProducts?.labels?.length &&
      dataProducts?.datasets?.[0]?.data?.length &&
      !isInitialLoadDone
    ) {
      setCenterValue({
        label: dataProducts.labels[0],
        value: `${dataProducts.datasets[0].data[0]}%`,
      });
      setIsInitialLoadDone(true); // prevent re-setting after hover
    }
  }, [dataProducts, isInitialLoadDone]);

  const optionsBattery = {
    plugins: {
      tooltip: {
        enabled: false,
        // callbacks: {
        //   label: (ctx) => `${ctx.label}: ${ctx.raw}%`,
        // },
      },
      legend: { display: false },
    },
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      easing: "easeOutCubic",
      duration: 200, // total chart load animation
    },
    hover: {
      animationDuration: 0, // <- remove delay on hover!
    },
    layout: {
      padding: 30, // <-- fixes clipping during hover
    },
    onHover: (event, chartElement) => {
      if (chartElement.length) {
        const index = chartElement[0].index;
        setHoverIndex(index);
        setCenterValueBattery({
          label: dataProductsBattery.labels[index],
          value: `${dataProductsBattery.datasets[0].data[index]}%`,
        });
      } else {
        setHoverIndex(null);
        setCenterValueBattery({
          label: dataProductsBattery?.labels[0],
          value: `${dataProductsBattery?.datasets[0]?.data[0]}%`,
        });
      }
    },
  };

  useEffect(() => {
    if (
      dataProductsBattery?.labels?.length &&
      dataProductsBattery?.datasets?.[0]?.data?.length &&
      !isInitialLoadDone
    ) {
      setCenterValueBattery({
        label: dataProductsBattery.labels[0],
        value: `${dataProductsBattery.datasets[0].data[0]}%`,
      });
      setIsInitialLoadDone(true); // prevent re-setting after hover
    }
  }, [dataProductsBattery, isInitialLoadDone]);

  useEffect(() => {
    generateDynamicJson();
    viewRecommandUserData();
  }, []);

  useEffect(() => {
    viewUserData();
  }, [pageCount]);

  useEffect(() => {
    viewMonthlyUserData();
  }, [selectedYear]);

  const onPageChange = (e) => {
    setPageCount(e);
  };

  const providerValue = {
    data,
    options,
    dataProducts,
    optionsProducts,
    centerValue,
    handleYearChange,
    selectedYear,
    monthWiseData,
    yearList,
    centerValueBattery,
    optionsBattery,
    dataProductsBattery,
    userData,
    onPageChange,
    pageCount,
    totalPage,
  };
  return (
    <DashBoardContext.Provider value={providerValue}>
      {children}
    </DashBoardContext.Provider>
  );
};
